import { useMutation } from "react-query";
import { useAppContext } from "../AppProviders";
import { API_URL } from "./helpers";

export interface BulkUploadValidateArgs {
  file: File;
  blueprintUUIDs: string;
  companyUUID: string;
  version?: number;
}

export interface BulkUploadError {
  row: number;
  column: string;
  message: string;
}

export function useValidateBulkCSV() {
  const { requestApi } = useAppContext();

  const uploadResume = async (
    csvFile: File,
    blueprintUUIDs: string,
    companyUUID: string,
    version?: number
  ): Promise<any> => {
    const formData = new FormData();
    formData.append("_file", csvFile);
    let endpoint = "reports/bulk/validate";
    if ((version ?? 1) === 2) {
      endpoint = `company/${companyUUID}/bulk/validate`;
    }
    return requestApi
      .post(
        `${API_URL}/${endpoint}?blueprint_uuids=${blueprintUUIDs}`,
        formData
      )
      .then((response) => {
        return response.data;
      });
  };

  const mutationResult = useMutation(
    (args: BulkUploadValidateArgs) =>
      uploadResume(
        args.file,
        args.blueprintUUIDs,
        args.companyUUID,
        args.version
      ),
    {}
  );

  return {
    ...mutationResult,
  };
}

export type BulkUploadInput = {
  file: File;
  companyUUID: string;
  blueprintUUIDs: string[];
  behaviorKitUUID?: string;
  clientQa?: boolean;
  internalQa?: boolean;
  pulseFrequency?: number;
  version?: number;
};

export function useUploadCSV() {
  const { requestApi } = useAppContext();

  const uploadCSV = async (input: BulkUploadInput): Promise<any> => {
    const formData = new FormData();
    formData.append("_file", input.file);
    const inputParams: {
      company_uuid: string;
      blueprint_uuids: string;
      behavior_kit_uuid?: string;
      client_qa?: string;
      internal_qa?: string;
      recur_type?: string;
      recur_value?: string;
    } = {
      company_uuid: input.companyUUID,
      blueprint_uuids: input.blueprintUUIDs.join(","),
      client_qa: input.clientQa ? "1" : "0",
      internal_qa: input.internalQa ? "1" : "0",
    };

    if (
      (input.behaviorKitUUID ?? "").length > 0 &&
      input.behaviorKitUUID !== "NULL"
    ) {
      inputParams["behavior_kit_uuid"] = input.behaviorKitUUID;
    }
    if (input.pulseFrequency && input.pulseFrequency > 0) {
      inputParams["recur_type"] = "interval";
      inputParams["recur_value"] = input.pulseFrequency.toString();
    }
    const params = new URLSearchParams(inputParams).toString();
    let endpoint = "reports/bulk";
    if ((input.version ?? 1) === 2) {
      endpoint = `company/${input.companyUUID}/bulk`;
    }
    return requestApi
      .post(`${API_URL}/${endpoint}?${params}`, formData)
      .then((response) => {
        return response.data;
      });
  };

  const mutationResult = useMutation(
    (input: BulkUploadInput) => uploadCSV(input),
    {}
  );

  return {
    ...mutationResult,
  };
}
